/** @jsx jsx */
import { jsx } from '@emotion/react'
import { css, SerializedStyles } from '@emotion/react/macro'
import {
  SimpleSelect,
  FilterSelect,
  MultiSelect,
  SimpleSelectProps,
  MultiSelectProps,
  FilterSelectProps
} from 'react-crane'

import {
  alternateGray,
  baseColor,
  borderColor,
  errorTextColor,
  primaryTextColor,
  secondaryGrayColor
} from '../../../../lib/styles/colors'
import { tertiaryBodyCss } from '../../../../lib/styles/typography'

export enum DropdownMode {
  Filter = 'filter',
  Multi = 'multi',
  Simple = 'simple'
}
export enum StyleVariant {
  Outline = 'outline',
  Filled = 'filled',
  Transparent = 'transparent'
}

export interface BaseDropdownProps {
  /** Indicates field is an error state */
  error?: boolean
  /** Type of dropdown to use */
  mode?: DropdownMode
  /** Indicates that label should be screen-ready only. */
  srOnlyLabel?: boolean
  /** Style variant of dropdown. */
  styleVariant?: StyleVariant
}

export interface DropdownProps extends BaseDropdownProps, SimpleSelectProps {}

export interface MultiDropdownProps extends BaseDropdownProps, MultiSelectProps {}

export interface FilterDropdownProps extends BaseDropdownProps, FilterSelectProps {}

type AllProps = DropdownProps | MultiDropdownProps | FilterDropdownProps

const Dropdown = ({ error, mode, srOnlyLabel, styleVariant, ...props }: AllProps) => {
  const { clearable } = { ...props }
  let Component

  switch (mode) {
    case DropdownMode.Filter:
      Component = FilterSelect
      break
    case DropdownMode.Multi:
      Component = MultiSelect
      break
    default:
      Component = SimpleSelect
      break
  }

  const filledCss = css`
    &.focus:not(.open) .crane-select-input-group,
    &:hover .crane-select-input-group {
      border: 1px solid ${error ? errorTextColor : baseColor};
    }

    .crane-select-value-group {
      padding-left: 16px;
      padding-top: 0;
    }

    &.open .crane-select-input-group:after {
      border-bottom: 1px solid ${borderColor};
      margin-left: 5px;
      margin-right: 5px;
    }

    .crane-select-input-group {
      background-color: #f5f6f9;
      border: 1px solid transparent;
      border-radius: 4px;
      padding: 10px;

      &:focus-within {
        background: white;
        border: 1px solid ${error ? errorTextColor : baseColor};
        border-bottom: none;
      }
    }

    .crane-select-input-group:after {
      border-bottom: none;
    }

    .crane-select-inner-input {
      position: relative;
      padding-left: 28px;
      width: 100%;
    }

    .crane-select-value-group {
      margin-top: 2px;
    }

    .crane-select-menu-container,
    .crane-select-no-results {
      border: 1px solid ${error ? errorTextColor : baseColor};
      border-top: none;
    }
  `

  const transparentCss = css`
    &.focus:not(.open) .crane-select-input-group,
    &:hover .crane-select-input-group {
      border: none;
    }

    .crane-select-input-group {
      border: none;
      background: none;
      cursor: pointer;
    }

    .crane-select-value-group {
      height: 47px;
      font-size: 18px;
      color: ${error ? errorTextColor : baseColor};
    }

    .crane-select-input-group {
      border: none;
    }

    .crane-select-arrow-container {
      display: none;
    }

    .crane-select-value-group > span {
      position: relative;
    }

    .crane-select-value-group > span:after {
      left: 0;
      bottom: 1px;
      width: 100%;
      position: absolute;
      content: '';
      border-bottom: 2px solid ${error ? errorTextColor : baseColor};
    }

    .crane-select-input-group:after {
      bottom: 6px;
    }

    .crane-select-menu-container {
      top: 40px;
    }
  `

  const dropdownCss = css`
    .crane-select-input-group,
    .crane-select-menu-container {
      ${tertiaryBodyCss}
      color: ${primaryTextColor}
    }

    .crane-select-option {
      padding: 12px;
    }

    .crane-select-option.focused {
      background: #eaeaea;
    }

    .crane-select-input-group:after {
      left: 0;
      right: 0;
      bottom: 0;
      content: '';
      position: absolute;
      transform: scaleX(0);
      transition: transform 200ms cubic-bezier(0, 0, 0.2, 1) 0ms;
      border-bottom: 2px solid ${error ? errorTextColor : baseColor};
      pointer-events: none;
    }

    &.focus:not(.open) .crane-select-input-group,
    &:hover .crane-select-input-group {
      border-color: ${secondaryGrayColor};
      border-bottom-color: rgba(0, 0, 0, 0.6);
      box-shadow: none;
    }

    &.open .crane-select-input-group:after,
    &.focus .crane-select-input-group:after {
      transform: scaleX(1);
    }

    .crane-select-inner-input {
      width: 100%;
    }

    .crane-select-input-group {
      font-size: 16px;
      line-height: 16px;
      padding: ${srOnlyLabel ? '16px 11px 11px' : '18px 11px 9px'};
      border-top-right-radius: 4px;
      border-top-left-radius: 4px;
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
      border: 1px solid ${secondaryGrayColor};
      border-bottom: 1px solid rgba(0, 0, 0, 0.3);
    }

    &.open .crane-select-input-group {
      padding-bottom: 12px !important;
    }

    .crane-select-input > input {
      padding-left: 0;
      height: 1.1875em;
    }

    .crane-select-value-group {
      font-size: 16px;
      line-height: 16px;
      padding-top: ${srOnlyLabel ? '19px' : '22px'};
      padding-left: 0;
      padding-right: ${clearable ? '60px' : '36px'};
      left: 11px;
    }

    .crane-select-menu-container {
      border-top: 0;
      margin-bottom: 20px;
    }

    .crane-select-clear-container {
      width: 60px;
    }

    .crane-select-clear {
      font-size: 20px;
    }

    .crane-select-arrow-container {
      position: absolute;
      right: 7px;
      top: calc(50% - 12px);
    }

    &.open .crane-select-arrow-container {
      top: calc(50% - 11px);
    }

    .crane-select-focus-placeholder {
      height: 17px;
    }

    .crane-select-placeholder {
      color: ${alternateGray};
    }
  `

  let styleVariantCss: SerializedStyles | null = null

  switch (styleVariant) {
    case StyleVariant.Filled:
      styleVariantCss = filledCss
      break
    case StyleVariant.Transparent:
      styleVariantCss = transparentCss
      break
    default:
      styleVariantCss = null
      break
  }

  return <Component {...props} css={[dropdownCss, styleVariantCss]} />
}

Dropdown.defaultProps = {
  error: false,
  labelKey: 'label',
  mode: DropdownMode.Simple,
  placeholder: '',
  styleVariant: StyleVariant.Outline,
  valueKey: 'value'
}

export default Dropdown
